import { detectEcommercePlatform, getCsvEcommerceSubtype, ConnectionPlatformInput, CsvConnectionInput } from './connectionPlatform';

export type ReportsExportSourceName =
  | 'Amazon Selling Partner'
  | 'Amazon CSV Import'
  | 'Shopify'
  | 'Shopify CSV Import';

/**
 * Maps a single connection to its Reports export display name, or null if it's not a
 * reportable connection (e.g. Veeqo -- a pooled inventory-overlay source with no SKU report
 * row of its own -- or a non-ecommerce CSV import). Exported so ReportsExportManager and its
 * tests share one implementation instead of each re-implementing platform detection.
 */
export function getReportsExportSourceName(
  connection: ConnectionPlatformInput & CsvConnectionInput
): ReportsExportSourceName | null {
  const csvSubtype = getCsvEcommerceSubtype(connection);
  if (csvSubtype === 'amazon') return 'Amazon CSV Import';
  if (csvSubtype === 'shopify') return 'Shopify CSV Import';

  const platform = detectEcommercePlatform(connection);
  if (platform === 'amazon') return 'Amazon Selling Partner';
  if (platform === 'shopify') return 'Shopify';
  return null;
}

/**
 * Builds the deduplicated list of Reports export data-source display names (plus a leading
 * "All" entry when there's more than one), or `['No supported connections']` when none exist.
 */
export function getReportsExportAvailableDataSources(
  connections: Array<ConnectionPlatformInput & CsvConnectionInput> | Record<string, unknown> | null | undefined
): string[] {
  const list: Array<ConnectionPlatformInput & CsvConnectionInput> = connections
    ? (Array.isArray(connections) ? connections : (Object.values(connections) as any[]))
    : [];

  const sources: string[] = [];
  list.forEach((connection) => {
    const name = getReportsExportSourceName(connection);
    if (name) sources.push(name);
  });

  const uniqueSources = [...new Set(sources)];
  if (uniqueSources.length > 1) uniqueSources.unshift('All');

  return uniqueSources.length > 0 ? uniqueSources : ['No supported connections'];
}
